// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The event log.
 *
 * It exists to make the privacy claim checkable rather than merely stated: everything runledger
 * did is listed, in order, and the list contains file NAMES, record COUNTS and TOKEN NUMBERS and
 * nothing else. If the tool ever read something it should not have, this is where it would show.
 *
 * There is no `console.log` anywhere in this codebase. `esbuild.drop` removes console calls from
 * the production bundle and a build gate asserts none survived, so this drawer is the only place
 * runledger says anything about itself.
 */

export type EventLevel = 'info' | 'good' | 'warn' | 'bad';

export interface LogEntry {
  at: string;
  level: EventLevel;
  message: string;
}

const entries: LogEntry[] = [];
let listEl: HTMLOListElement | null = null;

const MAX_ENTRIES = 500;

function stamp(): string {
  const d = new Date();
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
}

export function bindLog(el: HTMLOListElement): void {
  listEl = el;
  render();
}

export function logEvent(message: string, level: EventLevel = 'info'): void {
  entries.push({ at: stamp(), level, message });
  // A 500 MB file could otherwise produce tens of thousands of nodes and stall the drawer.
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  render();
}

export function logText(): string {
  return entries.map((e) => `[${e.at}] ${e.message}`).join('\n');
}

export function clearLog(): void {
  entries.length = 0;
  render();
}

function render(): void {
  if (!listEl) return;
  const frag = document.createDocumentFragment();
  for (const e of entries) {
    const li = document.createElement('li');
    li.className = `evt evt-${e.level}`;
    const t = document.createElement('span');
    t.className = 'evt-time';
    t.textContent = e.at;
    const m = document.createElement('span');
    m.className = 'evt-msg';
    // textContent, never innerHTML — a file name comes from the user's disk.
    m.textContent = e.message;
    li.append(t, m);
    frag.append(li);
  }
  listEl.replaceChildren(frag);
  listEl.scrollTop = listEl.scrollHeight;
}
